// 문서 탭(시안 renderKb 'doc') — 올리기·목록·기간/검색·선택 삭제, 처리 중이면 주기적으로 다시 읽기.
import { useEffect, useRef, useState } from "react";
import { listDocs, uploadDoc, deleteDoc, bulkDeleteDocs, errorText } from "./api";
import { showToast } from "../../../utill/utill";
import { ArmButton, DateBox, DropZone, docIcon, day, mb, nf, useDebounced } from "./parts";

const POLL_MS = 3000;
const ACCEPT = ".pdf,.txt,application/pdf,text/plain";

function StatusTag({ status }) {
    if (status === "processing") return <span className="tag tag-esc"><i className="fa-solid fa-spinner fa-spin"></i>처리 중</span>;
    if (status === "error") return <span className="tag tag-fail" title="처리하지 못했습니다. 지우고 다시 올려 주세요.">오류</span>;
    return <span className="tag tag-ok">반영됨</span>;
}

export default function Docs({ onChanged }) {
    const [q, setQ] = useState("");
    const qd = useDebounced(q);
    const [range, setRange] = useState({ from: "", to: "" });
    const [tick, setTick] = useState(0);
    const [docs, setDocs] = useState(null);
    const [pending, setPending] = useState([]);
    const [sel, setSel] = useState(() => new Set());
    const seq = useRef(0);
    const busyIds = useRef(new Set());

    useEffect(() => {
        const my = ++seq.current;
        listDocs({ q: qd.trim(), created_from: range.from, created_to: range.to })
            .then((list) => {
                if (my !== seq.current) return;
                const was = busyIds.current;
                const done = list.filter((d) => was.has(d.id) && d.status === "active");
                if (done.length) showToast("문서 반영이 끝났습니다. 이제 챗봇이 이 문서를 참고합니다.", "success");
                busyIds.current = new Set(list.filter((d) => d.status === "processing").map((d) => d.id));
                setDocs(list);
            })
            .catch((e) => { if (my === seq.current) { setDocs((d) => d || []); showToast(errorText(e), "error"); } });
    }, [qd, range, tick]);

    const processing = (docs || []).some((d) => d.status === "processing");
    useEffect(() => {
        if (!processing) return undefined;
        const t = setTimeout(() => setTick((n) => n + 1), POLL_MS);
        return () => clearTimeout(t);
    }, [processing, docs]);

    const reload = () => { setTick((n) => n + 1); onChanged && onChanged(); };

    async function upload(files) {
        if (!files.length) return;
        setPending(files.map((f) => f.name));
        let ok = 0;
        for (const f of files) {
            try { await uploadDoc(f); ok += 1; }
            catch (e) { showToast(`${f.name}: ${errorText(e)}`, "error"); }
            setPending((p) => p.slice(1));
        }
        if (ok) showToast(`문서 ${ok}개를 올렸습니다. 벡터화가 끝나면 챗봇 답변에 반영됩니다.`, "success");
        reload();
    }

    async function removeOne(id) {
        try {
            await deleteDoc(id);
            setSel((s) => { const n = new Set(s); n.delete(id); return n; });
            showToast("문서를 삭제했습니다.", "success");
            reload();
        } catch (e) { showToast(errorText(e), "error"); }
    }

    async function removeSel() {
        const ids = [...sel];
        try {
            const r = await bulkDeleteDocs(ids);
            setSel(new Set());
            showToast(`문서 ${r?.deleted ?? ids.length}개를 삭제했습니다. 챗봇은 더 이상 이 문서를 참고하지 않습니다.`, "success");
            reload();
        } catch (e) { showToast(errorText(e), "error"); }
    }

    const list = docs || [];
    const allOn = list.length > 0 && list.every((d) => sel.has(d.id));
    const toggle = (id, on) => setSel((s) => { const n = new Set(s); on ? n.add(id) : n.delete(id); return n; });
    const toggleAll = (on) => setSel((s) => { const n = new Set(s); list.forEach((d) => (on ? n.add(d.id) : n.delete(d.id))); return n; });

    return (
        <div className="pn">
            <DropZone accept={ACCEPT} multiple busy={pending.length > 0} onFiles={upload}>
                {(pick) => (<>
                    <div className="ic"><i className="fa-solid fa-cloud-arrow-up"></i></div>
                    <b>{pending.length ? "파일을 올리고 있습니다..." : "파일을 드래그하거나 클릭해서 업로드"}</b>
                    PDF, 텍스트 파일 지원 · 올리면 몇 분 안에 챗봇 답변에 반영됩니다<br />
                    <button className="btn btn-pri btn-sm" type="button" onClick={(e) => { e.stopPropagation(); pick(); }}>
                        <i className="fa-solid fa-file-arrow-up"></i>파일 선택
                    </button>
                </>)}
            </DropZone>
            <div className="pn-h" style={{ padding: "0 0 12px", border: 0 }}>
                <h3>업로드된 문서</h3>
                <span className="tag tag-res">{list.length}개 문서</span>
                <span className="sp"></span>
                <DateBox from={range.from} to={range.to} onChange={(from, to) => setRange({ from, to })} />
                <input className="inp" placeholder="문서 검색" value={q} onChange={(e) => setQ(e.target.value)} style={{ width: 200 }} />
                <ArmButton className="btn btn-sm btn-danger" disabled={!sel.size} style={sel.size ? undefined : { opacity: 0.45 }}
                    armedLabel={`정말 ${sel.size}개 삭제`} onConfirm={removeSel}>
                    <i className="fa-regular fa-trash-can"></i>선택 삭제{sel.size ? ` (${sel.size})` : ""}
                </ArmButton>
            </div>
            <div className="tbl-wrap" style={{ maxHeight: "none", minHeight: 0 }}>
                <table className="tbl">
                    <thead><tr>
                        <th style={{ width: 30 }}><input type="checkbox" title="전체 선택" checked={allOn} onChange={(e) => toggleAll(e.target.checked)} /></th>
                        <th>문서명</th><th className="num">챗봇 인용</th><th className="num">크기</th><th>업로드일</th><th>상태</th><th></th>
                    </tr></thead>
                    <tbody>
                        {pending.map((name, i) => {
                            const [icon, col] = docIcon(name);
                            return (
                                <tr key={`p${i}`}>
                                    <td></td>
                                    <td><div style={{ display: "flex", gap: 10, alignItems: "center" }}><i className={`fa-solid ${icon}`} style={{ color: col, fontSize: 20 }}></i><div><b>{name}</b><div style={{ color: "var(--tx3)", fontSize: 12 }}>올리는 중</div></div></div></td>
                                    <td className="num">-</td><td className="num">-</td><td className="c-dt">-</td>
                                    <td><StatusTag status="processing" /></td><td></td>
                                </tr>
                            );
                        })}
                        {list.map((d) => {
                            const [icon, col] = docIcon(d.original_name);
                            return (
                                <tr key={d.id}>
                                    <td><input type="checkbox" checked={sel.has(d.id)} onChange={(e) => toggle(d.id, e.target.checked)} /></td>
                                    <td><div style={{ display: "flex", gap: 10, alignItems: "center" }}><i className={`fa-solid ${icon}`} style={{ color: col, fontSize: 20 }}></i><div><b>{d.original_name}</b>{d.preview ? <div style={{ color: "var(--tx3)", fontSize: 12 }}>{d.preview}</div> : null}</div></div></td>
                                    <td className="num" title={d.last_cited_at ? `마지막 인용 ${day(d.last_cited_at)}` : undefined}>{nf(d.cited_count)}회</td>
                                    <td className="num">{mb(d.size)}</td>
                                    <td className="c-dt">{day(d.created_at)}</td>
                                    <td><StatusTag status={d.status} /></td>
                                    <td><ArmButton className="btn btn-sm btn-ghost btn-danger" title="삭제" armedLabel="삭제?" onConfirm={() => removeOne(d.id)}><i className="fa-regular fa-trash-can"></i></ArmButton></td>
                                </tr>
                            );
                        })}
                        {docs && !list.length && !pending.length && (
                            <tr><td colSpan={7}><div className="empty">조건에 맞는 문서가 없습니다{range.from || range.to ? " · 등록일 기간을 넓혀 보세요" : ""}</div></td></tr>
                        )}
                    </tbody>
                </table>
            </div>
            <div className="note" style={{ padding: "10px 0 0" }}>「챗봇 인용」은 답변을 만들 때 그 문서를 참고한 횟수입니다. 오래 인용되지 않는 문서는 내용이 낡았는지 확인하세요.</div>
        </div>
    );
}
